// ComposePost — compose form for creating a new post.
//
// Renders a form with:
// - Post type selector (project | post)
// - Title, excerpt, body (single textarea, split one BodyBlock per line), tags inputs
// - Conditional: external_url for posts (optional link)
// - Conditional: project_type + "Post to feed" checkbox for project posts
// - Submit that calls createPost(); surfaces submitting / success / error states.
//
// Design source: design/project/scripts/pages.jsx compose surface,
// design/project/CitiDev Hub.html compose form pattern.
//
// The default post type is "post" (most common action).
// Division is never sent by the client — the server derives it from the
// authenticated author's Member profile (issue #22).

import { type FormEvent, useCallback, useState } from "react";
import { createPost } from "./api";
import type {
  BodyBlock,
  PostCreate,
  PostResponse,
  PostType,
  ProjectStatus,
  ProjectType,
} from "./api";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Initial field values to pre-fill the form with — used in edit mode. */
export interface ComposeInitial {
  type?: PostType;
  title?: string;
  excerpt?: string;
  /** Existing body blocks; joined into the single body textarea for editing. */
  body?: BodyBlock[];
  tags?: string[];
  external_url?: string | null;
  project_type?: ProjectType | null;
  repo_url?: string | null;
  rideshare_url?: string | null;
  status?: ProjectStatus | null;
  posted_to_feed?: boolean;
}

export interface ComposePostProps {
  /**
   * Called after a post is successfully created (or edited, in edit mode).
   * Use this to navigate to the new post or refresh the feed/detail.
   */
  onSuccess?: (post: PostResponse) => void;
  /**
   * Default post type to preselect (e.g. "project" when opened from the
   * Projects page "Add Project" button). Defaults to "post".
   */
  defaultType?: PostType;
  /**
   * When true, locks the post type to `defaultType` and hides the type
   * selector — used by entry points scoped to a single content type (e.g.
   * "Add Project" should only ever create a project post). Always true in
   * edit mode: a post's type is immutable.
   */
  lockType?: boolean;
  /**
   * Pre-fill values. When provided the form opens in "edit mode": fields are
   * seeded, the submit button reads "Save changes", and the form is NOT
   * cleared on success (the edited values remain visible).
   */
  initial?: ComposeInitial;
  /**
   * Override the submit action. In edit mode this is wired to
   * `updatePost(id, …)`; when omitted the form creates a new post via
   * `createPost`. Receives the same `PostCreate` payload compose builds.
   */
  submitOverride?: (payload: PostCreate) => Promise<PostResponse>;
  /** Optional cancel affordance — renders a "Cancel" button when provided. */
  onCancel?: () => void;
}

const POST_TYPE_LABELS: Record<PostType, string> = {
  post: "Post",
  project: "Project",
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  poc: "POC",
  pilot: "Pilot",
  production: "Production",
};

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  Skill: "Skill",
  "MCP Server": "MCP Server",
  Plugin: "Plugin",
  Automation: "Automation",
  Application: "Application",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse comma-separated tags string into a clean string array. */
function parseTags(raw: string): string[] {
  return raw
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * ComposePost renders the new-post compose form.
 *
 * Controlled form; all fields clear on successful submission.
 * Submit is disabled while the request is in flight.
 *
 * Design token / CSS class reference (hub-feed.css, hub-compose-* namespace):
 *   .hub-compose-wrap   — outer container
 *   .hub-compose-form   — the <form> element
 *   .hub-compose-field  — label + input wrapper
 *   .hub-compose-select — type selector
 *   .hub-compose-input  — text input
 *   .hub-compose-textarea — multi-line textarea
 *   .hub-compose-submit — submit button
 *   .hub-compose-success — success message
 *   .hub-compose-error  — error message
 */
export function ComposePost({
  onSuccess,
  defaultType = "post",
  lockType = false,
  initial,
  submitOverride,
  onCancel,
}: ComposePostProps) {
  const isEdit = initial != null;
  // A post's type is immutable, so edit mode always locks the type selector.
  const typeLocked = lockType || isEdit;

  const [postType, setPostType] = useState<PostType>(initial?.type ?? defaultType);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [excerpt, setExcerpt] = useState(initial?.excerpt ?? "");
  // Compose stores the body as a single textarea, one line per BodyBlock —
  // mirrors design/project/scripts/pages.jsx's Compose modal, which splits on
  // "\n" so each typed line (including a "- " bullet line) becomes its own
  // block for BodyRenderer's list-grouping in PostDetail.tsx. Editing an
  // existing (possibly multi-block) post joins the blocks back with "\n" so
  // the round-trip through this textarea is symmetric with handleSubmit's split.
  const [body, setBody] = useState(
    () =>
      initial?.body
        ?.slice()
        .sort((a, b) => a.position - b.position)
        .map((b) => b.text)
        .join("\n") ?? "",
  );
  const [tags, setTags] = useState(initial?.tags?.join(", ") ?? "");
  const [externalUrl, setExternalUrl] = useState(initial?.external_url ?? "");
  const [projectType, setProjectType] = useState<ProjectType | "">(initial?.project_type ?? "");
  const [repoUrl, setRepoUrl] = useState(initial?.repo_url ?? "");
  const [rideshareUrl, setRideshareUrl] = useState(initial?.rideshare_url ?? "");
  const [status, setStatus] = useState<ProjectStatus>(initial?.status ?? "poc");
  const [postedToFeed, setPostedToFeed] = useState(initial?.posted_to_feed ?? false);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (submitting) return;

      setError(null);
      setSuccess(false);
      setSubmitting(true);

      const payload: PostCreate = {
        type: postType,
        title: title.trim(),
        excerpt: excerpt.trim(),
        body: body
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
          .map((text, position) => ({ position, text })),
        tags: parseTags(tags),
        external_url: postType === "post" && externalUrl.trim()
          ? externalUrl.trim()
          : null,
        project_type: postType === "project" && projectType ? projectType : null,
        repo_url: postType === "project" && repoUrl.trim()
          ? repoUrl.trim()
          : null,
        rideshare_url: postType === "project" && rideshareUrl.trim()
          ? rideshareUrl.trim()
          : null,
        status: postType === "project" ? status : null,
        posted_to_feed: postType === "project" ? postedToFeed : true,
      };

      try {
        const result = await (submitOverride ? submitOverride(payload) : createPost(payload));
        setSuccess(true);
        // On create, clear the form for the next post. On edit, keep the edited
        // values visible (the parent typically closes the form via onSuccess).
        if (!isEdit) {
          setTitle("");
          setExcerpt("");
          setBody("");
          setTags("");
          setExternalUrl("");
          setProjectType("");
          setRepoUrl("");
          setRideshareUrl("");
          setStatus("poc");
          setPostedToFeed(false);
        }
        onSuccess?.(result);
      } catch (err: unknown) {
        const verb = isEdit ? "save changes" : "create post";
        if (err && typeof err === "object" && "status" in err) {
          const status = (err as { status: number }).status;
          if (status === 401) {
            setError("You must be signed in to post.");
          } else if (status === 403) {
            setError("You can only edit your own posts.");
          } else if (status === 422) {
            setError("Please check your input — some fields are invalid.");
          } else {
            setError(`Could not ${verb}. Please try again.`);
          }
        } else {
          setError(`Could not ${verb}. Please try again.`);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      submitting,
      postType,
      title,
      excerpt,
      body,
      tags,
      externalUrl,
      projectType,
      repoUrl,
      rideshareUrl,
      status,
      postedToFeed,
      onSuccess,
      submitOverride,
      isEdit,
    ],
  );

  return (
    <div className="hub-compose-wrap" data-testid="compose-wrap">
      {success && (
        <div
          className="hub-compose-success"
          role="status"
          data-testid="compose-success"
          aria-live="polite"
        >
          {isEdit
            ? "Changes saved."
            : "Post published! It will appear in the feed shortly."}
        </div>
      )}

      {error && (
        <div
          className="hub-compose-error"
          role="alert"
          data-testid="compose-error"
          aria-live="assertive"
        >
          {error}
        </div>
      )}

      <form
        className="hub-compose-form"
        data-testid="compose-form"
        onSubmit={handleSubmit}
        aria-label="Compose a post"
      >
        {/* Post type selector — locked (hidden) when this compose form is
            opened from a type-scoped entry point, e.g. "Add Project". */}
        {typeLocked ? (
          <div className="hub-compose-field">
            <span className="hub-compose-label">Post type</span>
            <div className="hub-compose-static" data-testid="compose-type-static">
              {POST_TYPE_LABELS[postType]}
            </div>
          </div>
        ) : (
          <div className="hub-compose-field">
            <label htmlFor="compose-type" className="hub-compose-label">
              Post type
            </label>
            <select
              id="compose-type"
              className="hub-compose-select"
              data-testid="compose-type-select"
              value={postType}
              onChange={(e) => setPostType(e.target.value as PostType)}
              disabled={submitting}
            >
              <option value="post">Post</option>
              <option value="project">Project</option>
            </select>
          </div>
        )}

        {/* Title */}
        <div className="hub-compose-field">
          <label htmlFor="compose-title" className="hub-compose-label">
            Title <span aria-hidden="true">*</span>
          </label>
          <input
            id="compose-title"
            type="text"
            className="hub-compose-input"
            data-testid="compose-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Give your post a title…"
            required
            maxLength={512}
            disabled={submitting}
            aria-required="true"
          />
        </div>

        {/* Excerpt */}
        <div className="hub-compose-field">
          <label htmlFor="compose-excerpt" className="hub-compose-label">
            Summary <span aria-hidden="true">*</span>
          </label>
          <input
            id="compose-excerpt"
            type="text"
            className="hub-compose-input"
            data-testid="compose-excerpt"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="One-line summary of your post…"
            required
            disabled={submitting}
            aria-required="true"
          />
        </div>

        {/* Body */}
        <div className="hub-compose-field">
          <label htmlFor="compose-body" className="hub-compose-label">
            Body
          </label>
          <textarea
            id="compose-body"
            className="hub-compose-textarea"
            data-testid="compose-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write the full content of your post… one line per paragraph. Start a line with &quot;- &quot; for a bullet."
            rows={6}
            disabled={submitting}
          />
        </div>

        {/* Tags */}
        <div className="hub-compose-field">
          <label htmlFor="compose-tags" className="hub-compose-label">
            Tags
          </label>
          <input
            id="compose-tags"
            type="text"
            className="hub-compose-input"
            data-testid="compose-tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="ai, python, fastapi (comma-separated)"
            disabled={submitting}
          />
        </div>

        {/* External URL — posts only, optional */}
        {postType === "post" && (
          <div className="hub-compose-field">
            <label htmlFor="compose-external-url" className="hub-compose-label">
              External URL{" "}
              <span className="hub-compose-label--hint">(optional)</span>
            </label>
            <input
              id="compose-external-url"
              type="url"
              className="hub-compose-input"
              data-testid="compose-external-url"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://example.com/resource"
              disabled={submitting}
            />
          </div>
        )}

        {/* GitHub repo + artifact reference + status — project posts only */}
        {postType === "project" && (
          <>
            <div className="hub-compose-field">
              <label htmlFor="compose-status" className="hub-compose-label">
                Status
              </label>
              <select
                id="compose-status"
                className="hub-compose-select"
                data-testid="compose-status-select"
                value={status}
                onChange={(e) => setStatus(e.target.value as ProjectStatus)}
                disabled={submitting}
              >
                <option value="poc">POC</option>
                <option value="pilot">Pilot</option>
                <option value="production">Production</option>
              </select>
            </div>

            <div className="hub-compose-field">
              <label htmlFor="compose-repo-url" className="hub-compose-label">
                GitHub repo URL{" "}
                <span className="hub-compose-label--hint">(optional)</span>
              </label>
              <input
                id="compose-repo-url"
                type="url"
                className="hub-compose-input"
                data-testid="compose-repo-url"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="https://github.com/org/repo"
                disabled={submitting}
              />
            </div>

            <div className="hub-compose-field">
              <label htmlFor="compose-rideshare-url" className="hub-compose-label">
                Rideshare link{" "}
                <span className="hub-compose-label--hint">(optional)</span>
              </label>
              <input
                id="compose-rideshare-url"
                type="url"
                className="hub-compose-input"
                data-testid="compose-rideshare-url"
                value={rideshareUrl}
                onChange={(e) => setRideshareUrl(e.target.value)}
                placeholder="Your NBS Rideshare site link — for apps published there, not GitHub"
                disabled={submitting}
              />
            </div>

            <div className="hub-compose-field">
              <label htmlFor="compose-project-type" className="hub-compose-label">
                Type <span className="hub-compose-label--hint">(optional)</span>
              </label>
              <select
                id="compose-project-type"
                className="hub-compose-select"
                data-testid="compose-project-type-select"
                value={projectType}
                onChange={(e) => setProjectType(e.target.value as ProjectType | "")}
                disabled={submitting}
              >
                <option value="">— Select type —</option>
                {(Object.keys(PROJECT_TYPE_LABELS) as ProjectType[]).map((value) => (
                  <option key={value} value={value}>
                    {PROJECT_TYPE_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>

            <div className="hub-compose-field">
              <label className="hub-compose-label">
                <input
                  type="checkbox"
                  data-testid="compose-posted-to-feed"
                  checked={postedToFeed}
                  onChange={(e) => setPostedToFeed(e.target.checked)}
                  disabled={submitting}
                />{" "}
                Post to feed
              </label>
            </div>
          </>
        )}

        {/* Submit (+ optional Cancel) */}
        <div className="hub-compose-actions">
          <button
            type="submit"
            className="hub-compose-submit"
            data-testid="compose-submit"
            disabled={submitting}
            aria-disabled={submitting}
          >
            {isEdit
              ? submitting
                ? "Saving…"
                : "Save changes"
              : submitting
                ? "Publishing…"
                : "Publish post"}
          </button>
          {onCancel && (
            <button
              type="button"
              className="hub-compose-cancel"
              data-testid="compose-cancel"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default ComposePost;
